import { useCallback, useEffect, useState } from "react";
import { Users } from "lucide-react";
import { api, fmtUSD } from "@/lib/api";
import { CanvasTabs } from "@/components/meridian/CanvasTabs";
import { ActivityLog } from "@/components/meridian/ActivityLog";
import { KycChecklist } from "./KycChecklist";
import { KycScreening } from "./KycScreening";
import { KycRisk, KycSignoff } from "./KycRisk";

export const RATING = { Low: "bg-emerald-50 text-emerald-700 ring-emerald-200", Medium: "bg-amber-50 text-amber-700 ring-amber-200", High: "bg-red-50 text-red-700 ring-red-200" };
const STATUS = { "In review": "text-sky-700", Approved: "text-emerald-700", "Escalated - EDD": "text-amber-700", Declined: "text-red-700" };

function CaseCard({ c, active, onClick }) {
  return (
    <button data-testid={`kyc-case-${c.id}`} onClick={onClick}
      className={`w-full text-left p-3.5 rounded-xl border transition-colors ${active ? "bg-white border-sky-300 shadow-[0_1px_4px_rgba(2,132,199,0.15)]" : "border-slate-200 bg-white/60 hover:bg-white"}`}>
      <div className="flex justify-between items-start gap-2">
        <div className="text-[13px] font-semibold text-slate-900 leading-tight">{c.customer}</div>
        <span className={`chip ring-1 border-0 ${RATING[c.risk.rating]}`}>{c.risk.rating}</span>
      </div>
      <div className="text-[11px] text-slate-500 mt-1">{c.case_type} · <span className="num">{c.id}</span></div>
      <div className="flex justify-between text-[11px] mt-2">
        <span className="num text-slate-600">Docs {c.progress.docs_received}/{c.progress.docs_required} · Matches {c.progress.matches_pending} open</span>
        <span className={`font-medium ${STATUS[c.status]}`}>{c.status}</span>
      </div>
    </button>
  );
}

function Profile({ c }) {
  const row = (k, v) => <div className="flex justify-between gap-4 py-1.5 border-t border-slate-100 text-xs"><span className="text-slate-500">{k}</span><span className="text-slate-900 text-right">{v}</span></div>;
  return (
    <div className="panel p-5" data-testid="kyc-profile">
      <div className="eyebrow mb-2">Customer profile</div>
      {row("Entity type", c.entity_type)}{row("Industry", c.industry)}{row("Country", c.country)}
      {row("Expected monthly volume", fmtUSD(c.expected_monthly_volume, true))}{row("International share", `${Math.round(c.international_pct * 100)}%`)}
      {row("Products", c.products.join(", "))}{row("Relationship manager", c.relationship_manager)}
      <div className="eyebrow mt-4 mb-2 flex items-center gap-1.5"><Users className="w-3 h-3" />Beneficial owners</div>
      {c.owners.map((o) => <div key={o.name} className="flex justify-between text-xs py-1.5 border-t border-slate-100"><span className="text-slate-900">{o.name}<span className="text-slate-500"> · {o.role}</span></span><span className="num text-slate-700">{o.pct}%</span></div>)}
    </div>
  );
}

export default function KycCanvas({ task, tab, setTab, version }) {
  const [cases, setCases] = useState(null);
  const [sel, setSel] = useState(null);
  const load = useCallback(() => api.get("/kyc/cases").then((r) => { setCases(r.data); setSel((s) => s || r.data[0]?.id); }), []);
  useEffect(() => { load(); }, [load, version]);
  const c = cases?.find((x) => x.id === sel);
  const onCase = (updated) => setCases((cs) => cs.map((x) => (x.id === updated.id ? updated : x)));
  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="kyc-canvas">
      <div className="px-6 py-4 border-b border-slate-200">
        <div className="eyebrow">KYC agent · Customer due diligence</div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">{task.title}</h1>
      </div>
      <CanvasTabs tab={tab} setTab={setTab} tabs={[["dashboard", "Cases", cases?.filter((x) => x.status === "In review").length], ["activity", "Activity"]]} />
      <div className="flex-1 min-h-0 overflow-y-auto thin-scroll">
        {tab === "activity" && <ActivityLog taskId={task.id} version={version} />}
        {tab !== "activity" && (!cases ? <div className="p-6 text-sm text-slate-500">Loading cases…</div> : (
          <div className="p-6 grid xl:grid-cols-[280px_1fr] gap-5">
            <div className="space-y-2 xl:sticky xl:top-0 self-start" data-testid="kyc-case-list">{cases.map((x) => <CaseCard key={x.id} c={x} active={x.id === sel} onClick={() => setSel(x.id)} />)}</div>
            {c && (
              <div className="space-y-5 min-w-0" data-testid="kyc-case-detail">
                <div className="flex flex-wrap justify-between items-end gap-3">
                  <div><div className="num text-[11px] text-slate-500">{c.id} · opened {new Date(c.opened_at).toLocaleDateString("en-US")}</div><h2 className="font-display text-xl font-bold text-slate-900" data-testid="kyc-case-title">{c.customer}</h2></div>
                  <span data-testid="kyc-case-status" className={`text-sm font-semibold ${STATUS[c.status]}`}>{c.status}</span>
                </div>
                <div className="grid 2xl:grid-cols-2 gap-5">
                  <Profile c={c} />
                  <KycRisk c={c} />
                </div>
                <KycChecklist c={c} onCase={onCase} />
                <KycScreening c={c} onCase={onCase} />
                <KycSignoff key={c.id} c={c} onCase={onCase} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
